import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { refreshToken as fetchRefreshToken } from "./refreshTockenUtil";
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useNavigate } from "react-router-dom";
import { setLoginDetails } from "../../store/features/auth/authSlice";
import { RootState } from "../../store/store";
import environment from "../../environments/environment";

const TokenRefresher = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const refreshTimeout = useRef<NodeJS.Timeout | null>(null);
  const countdownInterval = useRef<NodeJS.Timeout | null>(null);
  const userDetails = useSelector((state: RootState) => state.auth);


  const clearRefreshTimeout = () => {
    if (refreshTimeout.current) {
      clearTimeout(refreshTimeout.current);
      refreshTimeout.current = null;
    }
  };

  const clearCountdownInterval = () => {
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }
  };

  const handleRefresh = async () => {
    try {
      const response = await fetchRefreshToken(userDetails.homeAccountId, dispatch, navigate);
      if (response && response.data.data) {
        const newResponseData = response.data.data;
        // Dispatch the new token expiration time to the store 
        const userInfo = {
          isLogin: true,
          jwtToken: newResponseData.accessToken,
          refreshToken: newResponseData.refreshToken,
          userName: `${newResponseData.userInfo.name}`,
          firstName: newResponseData.userInfo.givenName,
          lastName: newResponseData.userInfo.familyName,
          emailId: newResponseData.userInfo.displayableId,
          language: newResponseData.userInfo.language,
          menuPosition: newResponseData.userInfo.menuPosition,
          tenantId: newResponseData.userInfo.displayableId.split("@")[1],
          homeAccountId: newResponseData.userInfo.homeAccountId,
          expiresOn: newResponseData.expiresOn
        };
        dispatch(setLoginDetails(userInfo));
        scheduleTokenRefresh(newResponseData.expiresOn); // Schedule next refresh with new expiry time
      } else {
        throw new Error("Failed to fetch new token.");
      }

    } catch (err) {
      console.error("Error refreshing token", err);
      setError("Error refreshing token. Please login again.");
      setOpen(true);
      navigate(environment.ApplicationBaseName);
    }
  };

  const scheduleTokenRefresh = (expiresOn: string) => {
    const expiresOnDate = new Date(expiresOn);
    const now = new Date();
    const lifetime = expiresOnDate.getTime() - now.getTime(); // ms until expiration
    const refreshDelay = lifetime * 0.9; // 10% of token lifetime


    if (refreshDelay <= 0) {
      //console.warn("⚠️ Token already expired or close to expiry. Refreshing immediately.");
      handleRefresh();
      return;
    }

    clearRefreshTimeout();
    clearCountdownInterval();

    let countdown = Math.floor(refreshDelay / 1000);
    countdownInterval.current = setInterval(() => {
      if (countdown <= 0) {
        clearCountdownInterval();
      } else {
        countdown -= 1;
      }
    }, 1000);

    refreshTimeout.current = setTimeout(() => {
      clearCountdownInterval();
      handleRefresh();
    }, refreshDelay);
  };





  useEffect(() => {
    if (userDetails.isLogin) {
      // Pass true for test mode (forces 2-minute expiry)
      scheduleTokenRefresh(userDetails.expiresOn);
    }

    return () => {
      clearRefreshTimeout();
      clearCountdownInterval();
    };
  }, [userDetails.isLogin]);



  const handleClose = (
    event: React.SyntheticEvent<any> | Event,
    reason: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };


  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setOpen(false)} severity="error" sx={{ width: "100%" }}>
        {error}
      </Alert>
    </Snackbar>
  );
};

export default TokenRefresher;
